import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Loader2 } from "lucide-react";
import { findProvidersByIdentifierFn } from "@/lib/providers-secure.functions";
import { buildProviderSlug } from "@/lib/provider-slug";

type Field = "name" | "npi" | "license" | "phone" | "address";

const FIELDS: { id: Field; label: string }[] = [
  { id: "name", label: "Name" },
  { id: "npi", label: "NPI" },
  { id: "license", label: "License" },
  { id: "phone", label: "Phone" },
  { id: "address", label: "Address" },
];

type Suggestion = {
  id: string;
  name: string | null;
  practice: string | null;
  specialty: string | null;
  city: string | null;
  state: string | null;
};

export function FindMyPracticeButton({ className = "" }: { className?: string }) {
  const [field, setField] = useState<Field>("name");
  const [value, setValue] = useState("");
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const reqId = useRef(0);

  // Debounced fetch
  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const { rows } = await findProvidersByIdentifierFn({ data: { query: q, limit: 8 } });
        if (id !== reqId.current) return;
        setItems(rows as Suggestion[]);
        setOpen(true);
      } catch {
        if (id === reqId.current) setItems([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = () => {
    const q = value.trim();
    if (!q) return;
    setOpen(false);
    navigate({ to: "/search", search: { q } });
  };

  const pick = (s: Suggestion) => {
    setOpen(false);
    setValue(s.name || s.practice || "");
    navigate({
      to: "/providers/$id",
      params: { id: buildProviderSlug(s) },
    });
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        role="search"
        aria-label="Find my practice"
        className="flex items-stretch border border-border bg-card w-full"
      >
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => items.length && setOpen(true)}
          placeholder="Find My Practice"
          autoComplete="off"
          suppressHydrationWarning
          className="flex-1 min-w-0 basis-0 bg-transparent px-4 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none"
        />
        <div className="relative flex-1 basis-0 border-l border-border">
          <select
            value={field}
            onChange={(e) => setField(e.target.value as Field)}
            suppressHydrationWarning
            className="appearance-none w-full bg-transparent pl-3 pr-8 py-2 text-sm font-medium text-foreground outline-none cursor-pointer h-full"
            aria-label="Search by"
          >
            {FIELDS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        </div>
      </form>

      {open && (loading || items.length > 0) && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 border border-border bg-card shadow-lg max-h-80 overflow-auto">
          {loading && items.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </div>
          )}
          {items.map((s) => (
            <button
              type="button"
              key={s.id}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className="w-full text-left px-4 py-2 hover:bg-muted/60 border-b border-border last:border-b-0"
            >
              <div className="text-sm font-medium text-foreground truncate">
                {s.name || s.practice || "Unnamed provider"}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {[s.specialty, [s.city, s.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
          {!loading && value.trim().length >= 2 && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); submit(); }}
              className="w-full text-left px-4 py-2 text-xs font-semibold uppercase tracking-wider text-primary hover:bg-muted/60"
            >
              See all results for "{value.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
